import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, Checkbox, DatePicker, InputNumber, Modal, Select, Spin, Table, Tabs, Tag, Tooltip, message} from 'antd'
import {DownOutlined, DownloadOutlined, UpOutlined} from '@ant-design/icons'
import moment from 'moment'
import {Link} from 'react-router-dom'
import {
    exportWorkloadReport,
    getConfigHistory,
    getUrgencyRatios,
    getWorkloadParams,
    getOutputReport,
    getWorkloadReport,
    saveStageCapacity,
    saveUrgencyRatios,
    saveWorkloadParams,
} from '../../../../services/api/apiProductionPlanning'
import {
    CURRENT_OUTPUT_WARNING,
    DUE_GROUPS,
    TARGET_DAYS_TOOLTIP,
    WORKLOAD_PARAM_DEFAULTS,
    WORKLOAD_PARAM_RANGES,
    WORKLOAD_SOURCES,
    WORKLOAD_SOURCE_DESCRIPTIONS,
    stageConfigPath,
} from '../../constants'
import {
    CAPACITY_DATA_METRICS,
    CAPACITY_PARAM_GLOSSARY,
    CAPACITY_READING_NOTES,
    CAPACITY_RESULT_METRICS,
} from '../../constants/glossary'
import {DASH, formatDecimal, formatHours, formatInt, formatPercent, formatSigned} from '../../helpers/format'
import {lastWeekRange, presetRanges, rangeEndOf, rangeStartOf} from '../../helpers/dateRange'
import {downloadBlob, exportStamp} from '../../helpers/download'
import {errorCode, errorMessage} from '../../helpers/apiError'
import useUrlFilters from '../../../helpers/useUrlFilters'
import {canManageWorkloadParams} from '../../helpers/permission'
import Notice from '../shared/Notice'
import LoadError from '../shared/LoadError'
import PercentTag from '../shared/PercentTag'
import {CountUnitTag, PacingTag, TimeBasisTag} from '../shared/StageBadges'
import WorkloadParamsPanel from './WorkloadParamsPanel'
import StageDetailDrawer from './StageDetailDrawer'
import UrgencyRatioPanel from './UrgencyRatioPanel'
import AllocationSummaryLine from './AllocationSummaryLine'
import AllocationTab from './AllocationTab'
import ConfigSaveBar from './ConfigSaveBar'

const {Option} = Select
const {TabPane} = Tabs

/**
 * Khoá nguồn "Sản lượng hiện tại". Dùng hằng thay vì rải chuỗi: màn hình này đã có tiền lệ xấu là
 * `'received'` nằm rải rác ở bốn chỗ, và mỗi chỗ là một cơ hội gõ sai mà không có test nào bắt.
 */
const CURRENT_OUTPUT = 'currentOutput'

/**
 * Thời gian chờ trước khi tính lại báo cáo sau lần gõ cuối (ms).
 *
 * Một con số duy nhất cho MỌI ô của màn này — tỷ lệ mức khẩn, tham số nâng cao — để hành vi giống
 * nhau ở mọi chỗ. Ô nào cần ghi xuống hệ thống (số người/máy theo công đoạn) vẫn commit lúc rời ô:
 * đó là hành động lưu, không phải xem thử.
 */
const RECALC_DELAY_MS = 600

/**
 * Bộ tỷ lệ gửi lên: `{'g1.4': 50}` → `g1.4=50`.
 *
 * GỬI ĐỦ MỌI CỘT ĐANG CÓ TRONG STATE, kể cả cột bằng 100.
 *
 * ⚠️ Bản trước lọc bỏ mọi cột `=== 100` cho gọn query, và nó làm 100% thành giá trị KHÔNG ĐẶT ĐƯỢC
 *    từ màn hình, theo một đường vòng không ai nhìn ra:
 *
 *      · service gộp tỷ lệ ĐÃ LƯU với tỷ lệ của request theo TỪNG CỘT — `{...saved, ...query}`,
 *        request thắng ở cột nào nó có nói tới (`withSavedRatios`);
 *      · lọc cột `=== 100` đi nghĩa là chính những cột người dùng vừa kéo VỀ 100 là những cột
 *        request KHÔNG nói tới;
 *      · nên cột đó rơi về giá trị đã lưu. Kéo cả bảng lên 100% thì query rỗng trơn và TOÀN BỘ cấu
 *        hình đã lưu được áp lại — màn hình hiện 100.0 ở mọi ô mà số liệu vẫn là "2/6 item",
 *        "305/560 item" (đo trên máy người dùng 21/09/2026).
 *
 *    Hai quy ước đều đúng khi đứng riêng: "khoá thiếu = 100%" (BR-N3-05) và "cấu hình đã lưu là mặc
 *    định dùng chung". Ghép lại thì cái sau nuốt cái trước. Cách gỡ rẻ nhất và không đổi hợp đồng
 *    nào là để request phát biểu ĐỦ: cột nào màn hình đang hiện thì query nói ra cột đó.
 *
 * Map RỖNG vẫn trả `undefined` — lúc đó màn hình chưa nạp xong cấu hình dùng chung, và để service
 * áp mặc định của nhà máy mới là đúng.
 */
const serialiseRatios = (ratios) => {
    const pairs = Object.keys(ratios)
        .sort()
        .map((key) => `${key}=${ratios[key]}`)

    return pairs.length ? pairs.join(',') : undefined
}

/**
 * Ép kiểu các tham số SỐ về `number`, giữ nguyên mọi khoá khác.
 *
 * Mọi dữ liệu NGOÀI vào `params` phải đi qua đây. Chỉ ép ở đường gõ tay là chưa đủ: server trả
 * `reworkRate: "0.05"` thì chuỗi đó vào thẳng state rồi ra `GET /planning/capacity` nguyên dạng
 * chuỗi ngay lúc mount. Đo ở vòng review 19.
 *
 *   grep -n 'setParams(' <file này> | grep -v '^[0-9]*: *\*'
 *
 * Lệnh trên liệt kê các đường ghi. Đường đọc lại `savedParamsRef.current` ("Đặt lại") KHÔNG gọi
 * hàm này và không cần: ref chỉ nhận giá trị đã ép hoặc `WORKLOAD_PARAM_DEFAULTS` vốn toàn số.
 *
 * ⚠️ Chỉ ép các khoá SỐ ĐÃ BIẾT, không ép bừa mọi khoá. `params` không bị giới hạn ở
 *    `WORKLOAD_PARAM_DEFAULTS` — nó nhận thêm khoá lạ từ cấu hình đã lưu và từ lịch sử cấu hình,
 *    rồi những khoá đó chảy vào CẢ `queryParams` lẫn payload `saveWorkloadParams`. Ép bừa thì một
 *    tên cấu hình `"007"` thành `7`, `"0x10"` thành `16`. Đo ở vòng review 18.
 *
 * `hasOwnProperty.call` chứ không phải `key in` — `'toString' in {}` là `true`, nên `in` để lọt 12
 * khoá prototype và một khoá cấu hình tên `toString: "007"` vẫn bị ép thành `7`.
 *
 * Spread `{...acc}` chứ không phải `acc[key] =` — gán thẳng với `key === '__proto__'` kích hoạt
 * setter: khoá biến mất VÀ prototype của kết quả bị thay. Số khoá ở đây là vài đơn vị nên chi phí
 * spread không đáng kể; đừng đổi lại vì lý do hiệu năng.
 */
const coerceParams = (raw) => {
    const toNumber = (v) => (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))
        ? Number(v)
        : v)

    return Object.keys(raw).reduce((acc, key) => ({
        ...acc,
        [key]: Object.prototype.hasOwnProperty.call(WORKLOAD_PARAM_DEFAULTS, key)
            ? toNumber(raw[key])
            : raw[key],
    }), {})
}

/** Cận dưới của Khung thời gian mục tiêu. Là MẪU SỐ của phép tính nhân lực nên không được là 0. */
const TARGET_DAYS_MIN = 0.5

/**
 * Tỷ lệ mức khẩn rỗng — MỘT tham chiếu dùng chung.
 *
 * `useState({})` gọi hai lần cho `ratios` và `committedRatios` tạo hai object khác tham chiếu, nên
 * effect debounce `setCommittedRatios(ratios)` sau 600ms luôn đổi identity ⇒ `queryParams` đổi ⇒
 * thêm một lượt `GET /planning/capacity` mỗi lần mở màn, trên endpoint nặng nhất. Đo ở vòng 16.
 */
const EMPTY_RATIOS = {}

/**
 * Kẹp Khung thời gian mục tiêu về khoảng hợp lệ.
 *
 * Mọi giá trị `targetDays` ĐỌC TỪ NGOÀI phải đi qua đây — kể cả khi URL đổi SAU lúc mount
 * (Back/Forward, dán URL mới) — nếu không bốn nơi tiêu thụ (URL, ô nhập, câu gợi ý, `queryParams`)
 * sẽ nói bốn con số khác nhau. Xem lệnh ở cuối docblock để biết chính xác các call-site, đừng tin
 * bản liệt kê nào viết trong văn xuôi.
 *
 * Hai đường `setFilter` nội bộ — đổi nhóm cam kết và gõ tay vào ô — tự đảm bảo `>= TARGET_DAYS_MIN`
 * tại chỗ, không gọi hàm này.
 *
 * Kẹp về `TARGET_DAYS_MIN` chứ không phải mặc định `1`, để khớp đúng con số mà antd `InputNumber`
 * (`min={TARGET_DAYS_MIN}`) đang hiển thị trong ô.
 *
 * Call-site (lệnh tự loại dòng comment, kể cả dòng này):
 *   grep -n 'clampTargetDays(' <file này> | grep -v '^[0-9]*: *\*'
 */
const clampTargetDays = (value) => (
    Number.isFinite(value) && value >= TARGET_DAYS_MIN ? value : TARGET_DAYS_MIN
)

/** Khung thời gian mục tiêu tự điền theo nhóm cam kết NGẮN NHẤT đang chọn (§4.2). */
const autoTargetDays = (dueGroups) => {
    const selected = DUE_GROUPS.filter((group) => dueGroups.includes(group.value))

    if (!selected.length) return 1

    return Math.min(...selected.map((group) => group.days))
}

/**
 * SCR-M17-001 — Tính khối lượng công việc & nhân lực theo công đoạn.
 *
 * Một màn hình duy nhất. Không phân trang: bảng kết quả nhiều nhất 9 dòng nên hiển thị hết, và số
 * dòng do **routing** quyết định chứ không cố định trong mã nguồn (§5.5) — bố cục phải chịu được cả
 * trường hợp 2 dòng và 9 dòng.
 */
/**
 * Khoảng thời gian gợi ý sẵn cho ô "Thời gian nhận hàng".
 *
 * Tính CẢ hôm nay: "3 ngày" là hôm kia, hôm qua, hôm nay — không phải 3 ngày trước hôm nay. Đây là
 * cách người xưởng nói, và cũng khớp với ô mặc định (1 ngày = đúng hôm nay).
 *
 * Cố ý KHÔNG chặn mốc trên: người dùng vẫn chọn được ngày ở tương lai. Hàng có `created` ở tương lai
 * là bất thường nhưng có thật (lệch giờ máy quét), chặn đi thì họ không soi ra được.
 */
/** Chờ bấy nhiêu ms mới hiện spinner — dưới ngưỡng này thì lượt tải xong trước khi mắt kịp thấy. */
const SPIN_DELAY_MS = 200

function WorkloadPage({history, location}) {
    const canSaveParams = canManageWorkloadParams()

    // Bộ lọc nằm trên URL để copy link gửi cho người khác. `useMemo` giữ mốc thời gian mặc định cố
    // định trong suốt phiên, nếu không URL luôn bị nhét thêm tham số thừa.
    const filterDefaults = useMemo(() => ({
        // Mặc định là nguồn Sản lượng hiện tại (yêu cầu người dùng 16/09/2026).
        // ⚠️ Khác AC-17.1 của FAC-M17-SRS-002, vốn chốt mặc định là "Chưa phân lô" — cần sửa lại
        //    tài liệu, nếu không QA sẽ viết test theo câu cũ.
        source: CURRENT_OUTPUT,
        receivedFrom: moment().startOf('day'),
        receivedTo: moment().endOf('day'),
        skuIds: [],
        dueGroups: DUE_GROUPS.map((group) => group.value),
        targetDays: 1,
        autoTarget: true,
        /*
         * Lấy trọn đơn — BẬT mặc định, đúng D-N3-01.
         *
         * Khai ở `filterDefaults` nên nó đi vào URL và chỉ được ghi khi KHÁC mặc định: link chia sẻ
         * của một lượt xem thường vẫn sạch, còn lượt cố ý tắt thì người nhận mở ra thấy đúng cái
         * người gửi đang nhìn.
         */
        wholeOrder: true,
    }), [])
    const [filters, setFilter] = useUrlFilters(filterDefaults, {history, location})
    const {source, receivedFrom, receivedTo, skuIds, dueGroups, targetDays, autoTarget, wholeOrder} = filters
    const [showParams, setShowParams] = useState(false)
    const [params, setParams] = useState(WORKLOAD_PARAM_DEFAULTS)
    /** Bản tham số đã "chốt" để gọi API — tách khỏi `params` đúng như `committedRatios`. */
    const [committedParams, setCommittedParams] = useState(WORKLOAD_PARAM_DEFAULTS)
    /**
     * Bản `targetDays` đã "chốt" — tách khỏi `targetDays` đúng như `committedParams`.
     *
     * Thiếu lớp này thì mỗi ký tự gõ vào ô Khung thời gian mục tiêu là một lượt `GET
     * /planning/capacity` đầy đủ (`queryParams` → `fetchReport`), tức gõ "12" tốn 2 lượt trên
     * endpoint nặng nhất của màn — đúng thứ `RECALC_DELAY_MS` sinh ra để chặn cho các ô khác.
     *
     * ⚠️ Khởi tạo bằng `filters.targetDays` (đã parse từ URL), KHÔNG phải `filterDefaults`. Lấy
     *    hằng mặc định thì mở link `?targetDays=5` sẽ chạy lượt báo cáo ĐẦU bằng mẫu số 1 trong khi
     *    ô lọc đã hiện 5, tốn thêm một lượt GET, và mở cửa sổ đua vì `fetchReport` không có chốt
     *    chống response về trễ. Đo ở vòng review 15.
     */
    const [committedTargetDays, setCommittedTargetDays] = useState(
        () => clampTargetDays(filters.targetDays)
    )
    const [paramErrors, setParamErrors] = useState({})
    /** Bản tham số đã ghi xuống hệ thống — để rời ô mà không sửa gì thì không PUT lại. */
    const savedParamsRef = useRef(WORKLOAD_PARAM_DEFAULTS)

    const [report, setReport] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [activeTab, setActiveTab] = useState('stages')
    const [drawerStage, setDrawerStage] = useState(null)
    const [capacityDraft, setCapacityDraft] = useState({})
    const [exporting, setExporting] = useState(false)
    /**
     * Tỷ lệ mức khẩn — state CỤC BỘ, cố ý KHÔNG đưa vào `useUrlFilters`.
     *
     * BR-N3-07 / AC-17.5: đây là tham số của một lần chạy kịch bản, mỗi lần mở màn hình và mỗi lần
     * đổi nguồn rồi quay lại đều phải về 100%. Đưa lên URL là biến nó thành cấu hình sống dai qua
     * refresh và qua link gửi cho người khác — ngược hẳn ý định.
     */
    const [ratios, setRatios] = useState(EMPTY_RATIOS)
    /** Bản đã "chốt" để gọi API — tách khỏi `ratios` để gõ liên tục không bắn request mỗi ký tự. */
    const [committedRatios, setCommittedRatios] = useState(EMPTY_RATIOS)
    /**
     * "Lưu cấu hình cho lần sau" — CỐ Ý mặc định TẮT (yêu cầu người dùng, 2026-09-16): tỷ lệ mức
     * khẩn là tham số một kịch bản (AC-17.5), lưu lên server là hành động CHỦ Ý riêng, không phải hệ
     * quả tự động của việc gõ số. Bật rồi bấm "Lưu cấu hình" mới thực sự ghi xuống, dùng CHUNG cả nhà
     * máy — giống hệt `pp_workload_config`, không phải cấu hình riêng của người đang xem.
     */
    const [saveRatiosConfig, setSaveRatiosConfig] = useState(false)
    const [savingRatiosConfig, setSavingRatiosConfig] = useState(false)
    /**
     * Ai lưu cấu hình đang chạy và lưu lúc nào. Hiện thẳng ra màn hình chứ không giấu trong audit
     * log: cấu hình này dùng CHUNG cả nhà máy, nên câu hỏi đầu tiên khi thấy con số lạ luôn là "ai
     * đặt cái này" — không trả lời được thì người dùng không dám sửa, mà cũng không dám tin.
     */
    const [ratiosMeta, setRatiosMeta] = useState({savedBy: null, savedAt: null})
    /** Bản tỷ lệ ĐÃ LƯU, giữ riêng để nút Đặt lại đưa màn hình về đúng nó. */
    const savedRatiosRef = useRef({})

    // Tham số nâng cao: cùng cơ chế opt-in như tỷ lệ mức khẩn — xem `handleSaveParams`.
    const [saveParamsConfig, setSaveParamsConfig] = useState(false)
    const [savingParamsConfig, setSavingParamsConfig] = useState(false)
    const [paramsMeta, setParamsMeta] = useState({savedBy: null, savedAt: null})
    const [paramsName, setParamsName] = useState('')
    const [ratiosName, setRatiosName] = useState('')
    /**
     * Hộp thoại "nạp tỷ lệ làm lại từ dữ liệu thực tế".
     *
     * Hỏi khoảng thời gian TRƯỚC khi gọi, mặc định 7 ngày: tỷ lệ làm lại đo trên một ngày lẻ dao
     * động rất mạnh (một ca hỏng máy là cả ngày lệch), còn khoảng quá rộng lại kéo theo giai đoạn
     * mà quy trình đã khác. Bảy ngày là mức người dùng tự chỉnh được, không phải con số áp đặt.
     */
    const [actualRange, setActualRange] = useState(null)
    const [loadingActual, setLoadingActual] = useState(false)
    const [paramsHistory, setParamsHistory] = useState([])
    const [ratiosHistory, setRatiosHistory] = useState([])

    /** Nạp lịch sử một lần khi vào màn, và nạp lại sau mỗi lần lưu để bản vừa lưu có mặt ngay. */
    const reloadHistory = useCallback((kind) => {
        getConfigHistory(kind)
            .then((rows) => (kind === 'params' ? setParamsHistory(rows || []) : setRatiosHistory(rows || [])))
            .catch(() => undefined)
    }, [])

    useEffect(() => {
        reloadHistory('params')
        reloadHistory('urgencyRatios')
    }, [reloadHistory])

    useEffect(() => {
        getWorkloadParams()
            .then((saved) => {
                if (saved && saved.params) {
                    const loaded = coerceParams({...WORKLOAD_PARAM_DEFAULTS, ...saved.params})

                    setParams(loaded)
                    setCommittedParams(loaded)
                    savedParamsRef.current = loaded
                    setParamsMeta({savedBy: saved.savedBy || null, savedAt: saved.savedAt || null})
                }
            })
            .catch(() => undefined)
    }, [])

    /**
     * Trì hoãn tính lại sau khi gõ — áp CHUNG cho cả tỷ lệ mức khẩn lẫn tham số nâng cao.
     *
     * Trước đây tham số nâng cao tính lại NGAY mỗi lần đổi giá trị, nên sửa "số ca" rồi định sửa tiếp
     * "giờ/ca" là đã có một lượt tải báo cáo chen vào giữa: bảng nhấp nháy, và với dữ liệu thật thì
     * mỗi lượt tốn vài trăm mili giây — gõ xong bốn ô là bốn lượt tính cho ba trạng thái trung gian
     * không ai quan tâm.
     *
     * `RECALC_DELAY_MS` đủ dài để chuyển sang ô khác mà chưa bị tính chen, đủ ngắn để không thấy trễ
     * sau khi dừng tay.
     */
    useEffect(() => {
        const timer = setTimeout(() => {
            setCommittedRatios(ratios)
            setCommittedParams(params)
            setCommittedTargetDays(clampTargetDays(targetDays))
        }, RECALC_DELAY_MS)

        return () => clearTimeout(timer)
    }, [ratios, params, targetDays])

    /*
     * Chuẩn hoá `targetDays` MỘT LẦN Ở NGUỒN, không kẹp ở đầu ra.
     *
     * URL là nguồn của giá trị này, và cả ba nơi tiêu thụ đọc nó riêng: ô nhập (antd tự kẹp phần
     * HIỂN THỊ về `min`), câu gợi ý dưới bộ lọc (`formatDecimal(targetDays)` — không kẹp gì), và
     * `queryParams` gửi API. Kẹp ở đầu ra thì `?targetDays=0` cho ra BA con số khác nhau trên cùng
     * một màn (URL 0 · ô 0,5 · hint 0,0 · API 1) — đo ở vòng review 16. Ghi ngược giá trị đã kẹp
     * vào state + URL là cách duy nhất để bốn chỗ cùng nói một con số.
     */
    useEffect(() => {
        const clamped = clampTargetDays(targetDays)

        if (clamped !== targetDays) setFilter({targetDays: clamped})
    }, [targetDays, setFilter])

    // AC-17.5: rời nguồn này rồi quay lại thì mọi tỷ lệ về mặc định — KHÔNG còn chắc chắn là 100%
    // nữa, xem effect load cấu hình đã lưu ngay dưới đây (yêu cầu người dùng, 2026-09-16).
    useEffect(() => {
        if (source !== CURRENT_OUTPUT) {
            setRatios(EMPTY_RATIOS)
            setCommittedRatios(EMPTY_RATIOS)
            setSaveRatiosConfig(false)
            // Tab Phân bổ chỉ tồn tại ở nguồn này. Không đưa về tab mặc định thì `activeKey` trỏ vào
            // một TabPane vừa bị gỡ và người dùng nhìn thấy một vùng trắng, không hiểu vì sao.
            setActiveTab((current) => (current === 'allocation' ? 'stages' : current))
        }
    }, [source])

    /**
     * Vào nguồn Sản lượng hiện tại: nạp tỷ lệ đã lưu chung cả nhà máy làm mặc định của PHIÊN XEM này
     * (yêu cầu người dùng, 2026-09-16 — "lần sau không phải setting lại"). Không tự lưu ngược lại gì
     * cả: đây chỉ là ĐỌC, việc GHI vẫn cần bấm "Lưu cấu hình" bên dưới.
     */
    useEffect(() => {
        if (source !== CURRENT_OUTPUT) return

        getUrgencyRatios()
            .then((saved) => {
                const loaded = (saved && saved.ratios) || {}

                savedRatiosRef.current = loaded
                setRatiosMeta({savedBy: (saved && saved.savedBy) || null, savedAt: (saved && saved.savedAt) || null})

                if (Object.keys(loaded).length > 0) {
                    setRatios(loaded)
                    setCommittedRatios(loaded)
                }
            })
            .catch(() => undefined)
    }, [source])

    const queryParams = useMemo(() => ({
        source,
        receivedFrom: source === 'received' ? receivedFrom.toISOString() : undefined,
        receivedTo: source === 'received' ? receivedTo.toISOString() : undefined,
        skuIds: source === 'received' && skuIds.length ? skuIds : undefined,
        // Nhóm cam kết chỉ hiển thị và áp dụng cho nguồn "Chưa phân lô" (yêu cầu người dùng,
        // 2026-09-16). BR-N3-02: nguồn Sản lượng hiện tại tự phân loại theo mức khẩn, nhóm cam kết
        // không áp dụng. Nguồn "Thống kê theo thời gian" cũng KHÔNG gửi field này nữa — UI đã ẩn ô
        // chọn nên `dueGroups` trong state có thể còn giữ một lựa chọn cũ từ lúc ở nguồn khác; gửi
        // lên là áp một bộ lọc ẩn mà người dùng không thấy, đúng loại "nói dối người đọc log".
        dueGroups: source === 'unbatched' ? dueGroups : undefined,
        ratios: source === CURRENT_OUTPUT ? serialiseRatios(committedRatios) : undefined,
        // Chỉ gửi khi TẮT: mặc định bật nên không cần nói gì, và query sạch thì log dễ đọc hơn.
        wholeOrder: source === CURRENT_OUTPUT && !wholeOrder ? '0' : undefined,
        targetDays: committedTargetDays,
        ...committedParams,
    }), [source, receivedFrom, receivedTo, skuIds, dueGroups, committedRatios, committedTargetDays,
        committedParams, wholeOrder])

    /**
     * Khoá NỘI DUNG của bộ tham số, không phải identity của object.
     *
     * ⚠️ `queryParams` là object MỚI sau mỗi lần effect debounce commit lại `ratios`/`params`/
     *    `targetDays`, kể cả khi mọi giá trị y hệt. Dùng nó làm dependency thì `fetchReport` đổi
     *    identity và effect bắn lại một request giống hệt cái vừa gửi. Đo trên prod 03/11/2026:
     *    MỘT lần mở màn bắn **ba** lượt `source=currentOutput`, trong đó **hai cái tham số trùng
     *    khít** (3,80 s · 4,39 s), trên cụm Mongo dùng chung chỉ có một secondary. Lượt thứ ba là
     *    chính đáng — tỷ lệ đã lưu vừa nạp xong nên tham số thật sự đổi.
     */
    const queryKey = useMemo(() => JSON.stringify(queryParams), [queryParams])
    /*
     * Tham số đọc qua ref để `fetchReport` giữ identity ỔN ĐỊNH. Không có nó thì vừa bỏ
     * `queryParams` khỏi dependency vừa dùng giá trị cũ — đúng loại bug im lặng mà eslint
     * `exhaustive-deps` sinh ra khi bị tắt bằng tay.
     */
    const paramsRef = useRef(queryParams)

    paramsRef.current = queryParams

    /**
     * Số thứ tự lượt gọi. Response về MUỘN của một lượt đã bị bỏ không được ghi đè lượt mới.
     *
     * Không huỷ được request thật: repo đang ở axios 0.19, chưa có `signal`, và cắm `CancelToken`
     * vào `createApiServices` là đụng mọi màn của app. Bỏ qua response cũ là phần rẻ và an toàn của
     * cùng vấn đề — nó không cứu tải cho server, nhưng chặn được việc bảng hiện số của bộ lọc CŨ.
     */
    const runIdRef = useRef(0)

    const fetchReport = useCallback(async () => {
        const runId = runIdRef.current + 1

        runIdRef.current = runId
        setLoading(true)
        setError(null)

        try {
            const data = await getWorkloadReport(paramsRef.current)

            if (runIdRef.current === runId) setReport(data)
        } catch (e) {
            if (runIdRef.current === runId) setError(e)
        } finally {
            if (runIdRef.current === runId) setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchReport()
        // `queryKey` là NỘI DUNG tham số — xem docblock ở trên. Đổi sang `queryParams` là bắn trùng.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey, fetchReport])

    /**
     * Gõ tham số: chỉ đổi giá trị trong phiên xem và tính lại, KHÔNG ghi xuống hệ thống.
     *
     * `InputNumber` của antd bắn `onChange` theo từng ký tự. Ghi ngay tại đây thì gõ `12` sẽ lưu
     * `1` ra cấu hình dùng chung của cả nhà máy trước khi lưu `12`, và vì response không đảm bảo
     * thứ tự nên giá trị đọng lại có thể là chữ số dở dang. Việc ghi dồn về `commitParams`.
     */
    const handleParamChange = (patch) => {
        /*
         * Ép kiểu cho chính giá trị được LƯU, không chỉ cho vòng kiểm.
         *
         * antd `InputNumber` phát nguyên CHUỖI khi số chưa hoàn chỉnh (`"8."` lúc vừa gõ dấu thập
         * phân). Nếu chỉ ép kiểu trong vòng kiểm rồi `setParams(next)` với chuỗi thô, thì chuỗi đi
         * thẳng ra `GET /planning/capacity` (`clean()` không loại chuỗi) và vào cả payload
         * `saveWorkloadParams` nếu người dùng bấm Lưu mà chưa rời ô — tức ghi chuỗi vào cấu hình
         * dùng chung cả nhà máy. Đo ở vòng review 17.
         */
        const next = coerceParams({...params, ...patch})
        const errors = {}

        Object.keys(WORKLOAD_PARAM_RANGES).forEach((key) => {
            const range = WORKLOAD_PARAM_RANGES[key]
            // `next` đã được ép kiểu ở trên, nên chuỗi dở dang `"8."` đã thành `8`. Chuỗi KHÔNG
            // ép được (`""`, `"   "`, `"abc"`, `"8.5.5"`) giữ nguyên và rơi vào `!Number.isFinite`
            // bên dưới — đúng ý.
            const value = key === 'reworkRatePercent' ? next.reworkRate * 100 : next[key]

            /*
             * `!Number.isFinite` chứ KHÔNG phải `=== null || === undefined`.
             *
             * `reworkRatePercent` được tính bằng `next.reworkRate * 100`, mà `undefined * 100` ra
             * `NaN` — và `NaN < min` lẫn `NaN > max` ĐỀU false, `NaN !== null`, `NaN !== undefined`.
             * Với phép kiểm cũ thì xoá trắng ô "Tỷ lệ làm lại mặc định" không sinh lỗi nào: ô hiện
             * chữ "NaN" mà không viền đỏ, báo cáo chạy thiếu hẳn `reworkRate` (bị `clean()` loại),
             * và `handleSaveParams` — vốn chỉ chặn khi `paramErrors` khác rỗng — vẫn PUT được cấu
             * hình dùng chung cả nhà máy với trường đó bị nuốt mất. Đo ở vòng review 15.
             */
            if (!Number.isFinite(value) || value < range.min || value > range.max) {
                errors[key] = true
            }
        })

        setParamErrors(errors)

        // Ngoài khoảng hợp lệ thì KHÔNG áp dụng và không tính lại, giữ giá trị hợp lệ trước đó.
        if (Object.keys(errors).length) return

        setParams(next)
    }

    /**
     * Rời ô nhập (hoặc Enter): CHỈ tính lại báo cáo, KHÔNG ghi gì xuống hệ thống.
     *
     * Trước đây rời ô là tự động PUT cấu hình dùng chung cả nhà máy. Nghĩa là thử một kịch bản
     * "nếu chạy 2 ca thì cần bao nhiêu người" cũng âm thầm đổi tham số của MỌI người đang xem —
     * người dùng không hề phát ra ý định lưu. Nay lưu là hành động chủ ý riêng, giống hệt tỷ lệ mức
     * khẩn: tích ô rồi bấm nút.
     */
    const commitParams = () => undefined

    const handleSaveParams = () => {
        // Chốt quyền ở handler, giống `handleSaveUrgencyRatios` / `commitCapacity`. Gác ở JSX thôi
        // là chưa đủ: đây là ghi vào cấu hình DÙNG CHUNG cả nhà máy.
        if (!canSaveParams) return

        if (Object.keys(paramErrors).length) {
            message.error('Còn tham số ngoài khoảng hợp lệ — sửa lại trước khi lưu')

            return
        }

        setSavingParamsConfig(true)

        saveWorkloadParams({...params, name: paramsName.trim()})
            .then((saved) => {
                savedParamsRef.current = params
                setParamsMeta({
                    savedBy: (saved && saved.savedBy) || null,
                    savedAt: (saved && saved.savedAt) || null,
                })
                reloadHistory('params')
                message.success('Đã lưu tham số dùng chung — mọi người sẽ thấy cấu hình này ở lần mở sau')
            })
            .catch((e) => message.error(errorMessage(e) || 'Không lưu được tham số. Vui lòng thử lại'))
            .finally(() => setSavingParamsConfig(false))
    }

    const handleResetParams = () => {
        setParams(savedParamsRef.current)
        setCommittedParams(savedParamsRef.current)
        setParamErrors({})
        setSaveParamsConfig(false)
    }

    const toggleDueGroup = (value) => {
        const next = dueGroups.includes(value)
            ? dueGroups.filter((item) => item !== value)
            : [...dueGroups, value]

        // Không cho bỏ chọn thẻ cuối cùng đang bật. Không hiện thông báo lỗi, chỉ đơn giản không
        // phản hồi (§4.3 · V-03).
        if (!next.length) return

        setFilter({
            dueGroups: next,
            // Nhóm thời gian đổi thì số ngày mục tiêu chạy theo, trừ khi người dùng đã tự nhập.
            ...(autoTarget ? {targetDays: autoTargetDays(next)} : {}),
        })
    }

    /** Gõ số người / số máy đang có: chỉ ghi vào bản nháp tại chỗ. */
    const changeCapacity = (stageCode, key, value) => {
        setCapacityDraft((current) => ({...current, [`${stageCode}.${key}`]: value}))
    }

    /**
     * Ô tỷ lệ làm lại của công đoạn — giữ nháp theo PHẦN TRĂM, gửi đi theo TỶ SỐ.
     *
     * Người dùng nghĩ và gõ "5", server lưu 0,05 (cùng đơn vị với tỷ lệ chung, để hai đường không
     * phải quy đổi qua lại ở tầng tính toán). Quy đổi gói gọn ở đây thay vì rải ra: một chỗ quên
     * nhân 100 là tỷ lệ lệch 100 lần mà không có gì báo.
     */
    const reworkPercentValue = (row) => {
        const draftKey = `${row.stageCode}.reworkRate`

        if (Object.prototype.hasOwnProperty.call(capacityDraft, draftKey)) {
            return capacityDraft[draftKey]
        }

        return row.stageReworkRate === null || row.stageReworkRate === undefined
            ? undefined
            : Number((row.stageReworkRate * 100).toFixed(1))
    }

    /**
     * Gỡ một khoá khỏi bản nháp để ô rơi về giá trị của server.
     *
     * Bắt buộc gọi khi PUT hỏng: `capacityValue` luôn ưu tiên `capacityDraft` hơn `row[key]`, nên
     * không gỡ thì ô vẫn hiện số người dùng vừa gõ trong khi server giữ số cũ — và bản nháp sai đó
     * sống qua cả lần tải lại báo cáo. Người dùng rời màn tin là đã lưu; lần sau mở lại thấy số cũ
     * và đọc thành "hệ thống mất dữ liệu".
     */
    const dropDraft = (draftKey, sentValue) => {
        setCapacityDraft((current) => {
            if (!Object.prototype.hasOwnProperty.call(current, draftKey)) return current

            /*
             * Chỉ gỡ khi nháp VẪN ĐÚNG giá trị đã gửi đi.
             *
             * Xoá vô điều kiện thì ca này mất dữ liệu im lặng: người dùng rời ô (PUT bay), thấy gõ
             * nhầm nên bấm lại và sửa tiếp, rồi PUT CŨ trượt — `.catch` xoá luôn khoá đang giữ giá
             * trị MỚI, ô nhảy về số của server, và lần rời ô sau `commitCapacity` thoát ngay ở
             * `hasOwnProperty` nên KHÔNG có PUT nào cho giá trị mới. Đo ở vòng review 15.
             */
            if (current[draftKey] !== sentValue) return current

            const next = {...current}

            delete next[draftKey]

            return next
        })
    }

    /**
     * Rời ô mới ghi xuống hệ thống rồi tính lại báo cáo. Ghi theo từng ký tự như trước thì mỗi
     * chữ số là một PUT cấu hình dùng chung cộng một lần tải lại toàn bộ báo cáo.
     */
    const commitReworkRate = (row) => {
        if (!canSaveParams) return

        const draftKey = `${row.stageCode}.reworkRate`

        if (!Object.prototype.hasOwnProperty.call(capacityDraft, draftKey)) return

        const percent = capacityDraft[draftKey]
        // Bỏ trống = xoá tỷ lệ riêng, quay về dùng chung. KHÔNG đổi thành 0: 0% nghĩa là công đoạn
        // này không bao giờ làm lại, một khai báo khác hẳn "chưa đặt".
        const next = percent === undefined || percent === null || percent === '' ? null : percent / 100

        if (next === (row.stageReworkRate ?? null)) return

        saveStageCapacity(row.stageCode, {reworkRate: next})
            .then(() => fetchReport())
            .catch((e) => {
                dropDraft(draftKey, percent)
                message.error(errorMessage(e) || 'Không lưu được tỷ lệ làm lại. Vui lòng thử lại')
            })
    }

    const commitCapacity = (row, key) => {
        if (!canSaveParams) return

        const draftKey = `${row.stageCode}.${key}`

        if (!Object.prototype.hasOwnProperty.call(capacityDraft, draftKey)) return

        const value = capacityDraft[draftKey]

        if (value === row[key]) return

        saveStageCapacity(row.stageCode, {[key]: value === undefined ? null : value})
            .then(() => fetchReport())
            .catch((e) => {
                dropDraft(draftKey, value)
                message.error(errorMessage(e) || 'Không lưu được giá trị. Vui lòng thử lại')
            })
    }

    const capacityValue = (row, key) => {
        const draftKey = `${row.stageCode}.${key}`

        return Object.prototype.hasOwnProperty.call(capacityDraft, draftKey)
            ? capacityDraft[draftKey]
            : row[key]
    }

    const handleExport = async () => {
        setExporting(true)

        try {
            // Phải tự dựng link tải: `await` không thôi thì blob bị bỏ đi mà vẫn báo thành công.
            downloadBlob(await exportWorkloadReport(queryParams), `hoach-dinh-nang-luc-${exportStamp()}.xlsx`)
            message.success('Đã tạo file Excel 5 sheet')
        } catch (e) {
            message.error(errorMessage(e) || 'Xuất file thất bại. Vui lòng thử lại')
        } finally {
            setExporting(false)
        }
    }

    const stages = (report && report.stages) || []
    const totals = (report && report.totals) || {}
    // Số item bị kéo thêm vào vì nằm chung đơn với item đã chọn (BR-N3-19). Chỉ nguồn "Sản lượng hiện
    // tại" có phân bổ, hai nguồn kia không trả `allocation` nên về 0.
    const pulledItemCount = (report && report.allocation && report.allocation.totals.pulled) || 0

    /**
     * "Thêm số lượng tương ứng" (yêu cầu người dùng, 2026-09-16) — số item GỐC của mỗi ô mức khẩn
     * (trước khi áp tỷ lệ), gộp từ `allocation.cells` (một dòng theo SKU × ô) về theo ô. Tính ở FE
     * thay vì đổi API: dữ liệu đã có sẵn trong response hiện tại, không cần round-trip thêm.
     */
    const urgencyCounts = useMemo(() => {
        const cells = (report && report.allocation && report.allocation.cells) || []

        return cells.reduce((acc, cell) => {
            const key = `${cell.groupKey}.${cell.columnKey}`
            const entry = acc[key] || {qty: 0, target: 0, actual: 0}

            entry.qty += cell.qty
            // `target` = số lượng MỤC TIÊU, tức chính tỷ lệ người dùng vừa gõ đã làm tròn lên theo
            // từng SKU. Đây là con số trả lời đúng câu hỏi "gõ 20% thì ra bao nhiêu item".
            entry.target += cell.target
            // `actual` = số thực tế, đã cộng phần bị kéo thêm do lấy trọn đơn. Ở dữ liệu thật phần
            // này có thể lớn hơn `target` rất nhiều (đo 16/09/2026: cột "≥ 4 ngày" đặt 20% ra mục
            // tiêu 92 nhưng thực tế 453, do 361 item bị kéo theo đơn). Giữ lại để nói ra chênh lệch,
            // KHÔNG dùng làm con số chính cạnh ô nhập — cạnh ô nhập phải là con số của chính tỷ lệ đó.
            entry.actual += cell.actual
            acc[key] = entry

            return acc
        }, {})
    }, [report])

    /**
     * Đọc sản lượng thực tế trong khoảng đã chọn, suy ra tỷ lệ làm lại của từng công đoạn rồi GHI
     * luôn xuống cấu hình.
     *
     * Dùng lại đúng báo cáo của màn Kiểm soát sản lượng (`getOutputReport`) thay vì thêm một endpoint
     * mới: con số cần tính chính là `redoCount / output` theo công đoạn, mà báo cáo đó đã trả về sẵn
     * cả hai. Thêm một đường tính thứ hai cho cùng một đại lượng là thêm một chỗ để hai màn hình lệch
     * nhau.
     *
     * GHI luôn chứ không chỉ điền vào ô: các ô này vốn lưu lúc rời ô, mà điền bằng mã thì không có
     * lần rời ô nào — để nguyên thì người dùng thấy số mới trên màn hình nhưng không có gì được lưu.
     * Hộp thoại xác nhận phía trước chính là bước đồng ý cho việc ghi này.
     */
    const handleLoadActualRework = () => {
        // Chốt quyền ở handler: hàm này ghi HÀNG LOẠT `saveStageCapacity` cho mọi công đoạn.
        if (!canSaveParams) return

        const [rangeFrom, rangeTo] = actualRange || []

        if (!rangeFrom || !rangeTo) return

        setLoadingActual(true)

        getOutputReport({from: rangeFrom.toISOString(), to: rangeTo.toISOString()})
            .then((report18) => {
                const byStage = new Map()

                for (const entry of (report18 && report18.actors) || []) {
                    for (const stage of entry.stages || []) {
                        const acc = byStage.get(stage.stageCode) || {redoCount: 0, output: 0}

                        acc.redoCount += stage.redoCount || 0
                        acc.output += stage.output || 0
                        byStage.set(stage.stageCode, acc)
                    }
                }

                const writes = stages
                    .map((stage) => {
                        const measured = byStage.get(stage.stageCode)

                        // Công đoạn không có sản lượng nào trong khoảng đó thì KHÔNG đặt gì — ghi 0
                        // sẽ là khẳng định "công đoạn này không bao giờ làm lại", một điều ta không
                        // hề đo được.
                        if (!measured || !measured.output) return null

                        return {
                            stageCode: stage.stageCode,
                            rate: Math.round((measured.redoCount / measured.output) * 1000) / 1000,
                        }
                    })
                    .filter(Boolean)

                if (!writes.length) {
                    message.warning('Khoảng thời gian này không có sản lượng nào để đo tỷ lệ làm lại')

                    return
                }

                setCapacityDraft((current) => writes.reduce(
                    (acc, item) => ({...acc, [`${item.stageCode}.reworkRate`]: Math.round(item.rate * 1000) / 10}),
                    current
                ))

                /*
                 * Mỗi lượt ghi TỰ nuốt lỗi của mình rồi mới gom — KHÔNG dùng `Promise.all` trần.
                 *
                 * Với `all`, một công đoạn trượt là reject cả chùm rồi rơi xuống `.catch` chung bên
                 * dưới — vốn là catch của bước ĐỌC. Hậu quả đo được ở vòng review 14: người dùng
                 * thấy "Không đọc được sản lượng thực tế" trong khi việc đọc đã xong; hộp thoại
                 * không đóng vì `setActualRange(null)` nằm trong `.then`; và các PUT đã thành công
                 * vẫn nằm lại nên cấu hình dùng chung ở trạng thái ghi dở dang.
                 *
                 * ⚠️ Cố ý KHÔNG dùng `Promise.allSettled`: nó là ES2020, không có trên Safari 12.1
                 *    — vẫn nằm trong `browserslist` của repo — và CRA ở đây không nạp polyfill nào
                 *    (`grep react-app-polyfill\|core-js src/index.js` → rỗng). Đúng cái bẫy mà
                 *    `blobToText` / `readFileAs` được viết ra để tránh.
                 */
                let firstError = null

                return Promise.all(writes.map((item) => saveStageCapacity(item.stageCode, {reworkRate: item.rate})
                    .then(() => null)
                    .catch((e) => {
                        // Giữ MỘT lỗi đại diện. `errorMessage` chỉ trả câu thông báo của service;
                        // mã nghiệp vụ là việc của `errorCode`, nên ghép cả hai — lỗi chỉ mang mã
                        // (503 `UPSTREAM_DOWN`) mà không mang message thì vẫn nói được gì đó.
                        if (!firstError) firstError = errorMessage(e) || errorCode(e)

                        return item
                    })))
                .then((results) => {
                    const failed = results.filter(Boolean)

                    // Gỡ bản nháp lạc quan của đúng những công đoạn trượt, để ô rơi về giá trị
                    // server. `sentValue` phải khớp ĐÚNG giá trị đã nhét vào nháp ở trên — thiếu nó
                    // thì guard `current[draftKey] !== sentValue` luôn đúng và không gỡ gì cả.
                    failed.forEach((item) => dropDraft(
                        `${item.stageCode}.reworkRate`,
                        Math.round(item.rate * 1000) / 10
                    ))

                    setActualRange(null)
                    fetchReport()

                    if (!failed.length) {
                        message.success(`Đã nạp tỷ lệ làm lại thực tế cho ${writes.length} công đoạn`)

                        return
                    }

                    // Nêu TÊN công đoạn: lưới ngay bên cạnh hiển thị `stage.name`, người dùng
                    // không tra được mã viết tắt. Giữ lại một thông báo lỗi đại diện để không mất
                    // mã nghiệp vụ (403 thiếu quyền, 503 chưa đấu nối…).
                    const nameOf = (code) => {
                        const found = stages.find((stage) => stage.stageCode === code)

                        return (found && found.name) || code
                    }

                    message.error(
                        `Đã ghi ${writes.length - failed.length}/${writes.length} công đoạn. `
                        + `Không ghi được: ${failed.map((item) => nameOf(item.stageCode)).join(', ')}`
                        + (firstError ? ` — ${firstError}` : '')
                    )
                })
            })
            .catch((e) => message.error(errorMessage(e) || 'Không đọc được sản lượng thực tế. Vui lòng thử lại'))
            .finally(() => setLoadingActual(false))
    }

    const handleSaveUrgencyRatios = () => {
        // Chốt quyền ở cả handler, giống `commitReworkRate` / `commitCapacity`. Gác ở JSX thôi là
        // chưa đủ: đây là ghi vào cấu hình DÙNG CHUNG cả nhà máy.
        if (!canSaveParams) return

        setSavingRatiosConfig(true)

        saveUrgencyRatios(serialiseRatios(ratios), ratiosName.trim())
            .then((saved) => {
                savedRatiosRef.current = {...ratios}
                setRatiosMeta({
                    savedBy: (saved && saved.savedBy) || null,
                    savedAt: (saved && saved.savedAt) || null,
                })
                reloadHistory('urgencyRatios')
                message.success('Đã lưu tỷ lệ mức khẩn dùng chung — mọi người sẽ thấy cấu hình này ở lần mở sau')
            })
            .catch((e) => message.error(errorMessage(e) || 'Không lưu được cấu hình. Vui lòng thử lại'))
            .finally(() => setSavingRatiosConfig(false))
    }

    /**
     * Đặt lại — thay cho nút "Xoá cấu hình đã lưu" trước đây.
     *
     * Nút cũ ghi đè cấu hình dùng CHUNG bằng tập rỗng: một người muốn dọn màn hình của mình lại xoá
     * mất cấu hình của cả nhà máy, và không có đường lùi. Nút này chỉ đưa MÀN HÌNH ĐANG XEM về bản
     * đã lưu (chưa ai lưu thì về 100%), không đụng gì tới server.
     */
    const handleResetUrgencyRatios = () => {
        const saved = savedRatiosRef.current || {}

        setRatios(saved)
        setCommittedRatios(saved)
        setSaveRatiosConfig(false)
    }

    const numberCell = (value, render) => (value === null || value === undefined
        ? <span className="VcMuted">{DASH}</span>
        : render(value))

    const columns = [
        {
            title: 'Công đoạn',
            key: 'name',
            width: 145,
            render: (text, row) => (
                <div>
                    <strong>{row.name}</strong>
                    {report && report.bottleneckStageCode === row.stageCode && (
                        <Tag color="red" className="ml-1">NGHẼN</Tag>
                    )}
                    {row.stageCode === 'chupanh' && (
                        <Tag color="blue" className="ml-1">chỉ hàng cần chụp ảnh</Tag>
                    )}
                </div>
            ),
        },
        {
            title: (
                <Tooltip title="Nhịp công đoạn — Tốc độ công đoạn này do MÁY hay do NGƯỜI quyết định. Công đoạn theo máy thì thêm người không làm nhanh hơn">
                    <span className="VcHasTooltip">Nhịp</span>
                </Tooltip>
            ),
            dataIndex: 'pacing',
            width: 75,
            render: (pacing) => <PacingTag pacing={pacing}/>,
        },
        {
            title: (
                <Tooltip title="Số lượt qua công đoạn — Số item hoặc số đơn phải đi qua công đoạn này, theo đúng đơn vị đếm sản lượng. Ở In, Cắt, Khắc luôn đếm theo item, không đếm theo dòng vật liệu">
                    <span className="VcHasTooltip">Lượt qua</span>
                </Tooltip>
            ),
            dataIndex: 'throughput',
            align: 'right',
            width: 55,
            render: formatInt,
        },
        {
            title: (
                <Tooltip title="Đơn vị đếm sản lượng — Con số ở cột Lượt qua đếm theo đơn vị này. Ở In, Cắt, Khắc luôn đếm theo sản phẩm, không đếm theo dòng vật liệu">
                    <span className="VcHasTooltip">Đơn vị</span>
                </Tooltip>
            ),
            dataIndex: 'countUnit',
            width: 60,
            render: (countUnit) => <CountUnitTag countUnit={countUnit}/>,
        },
        {
            title: (
                <Tooltip title="Cách tính giờ — Cách ra con số giờ, độc lập với đơn vị đếm. Theo sản phẩm = định mức nhân 1. Theo dữ liệu file in = cộng theo từng dòng vật liệu của sản phẩm">
                    <span className="VcHasTooltip">Tính giờ</span>
                </Tooltip>
            ),
            key: 'timeBasis',
            /*
             * 140 chỉ đủ cho CÁI THẺ, và đó là chủ đích: tên đại lượng (`cutting_length`, 101px)
             * xuống dòng dưới thẻ. Để cặp đó cùng một dòng thì cột phải 245px — 105px cho một mã
             * kỹ thuật, trên cái bảng vừa dọn xong cuộn ngang. Xem `.VcTimeBasis--stacked`.
             */
            width: 140,
            render: (text, row) => <TimeBasisTag timeBasis={row.timeBasis} detectField={row.detectField} stacked/>,
        },
        {
            title: (
                <Tooltip title="Giờ công theo định mức — Số giờ cần để làm hết lượng hàng theo đúng định mức, chưa cộng phần làm lại">
                    <span className="VcHasTooltip">Giờ định mức</span>
                </Tooltip>
            ),
            dataIndex: 'standardHours',
            align: 'right',
            width: 65,
            render: (value) => formatHours(value, 1),
        },
        {
            title: (
                <Tooltip title="Giờ công đã tính làm lại — Giờ công theo định mức × (1 + tỷ lệ làm lại). Tỷ lệ làm lại đặt ở Tham số nâng cao">
                    <span className="VcHasTooltip">Giờ + làm lại</span>
                </Tooltip>
            ),
            dataIndex: 'reworkedHours',
            align: 'right',
            width: 60,
            render: (value) => formatHours(value, 1),
        },
        {
            title: (
                <Tooltip title="Số máy cần — Làm tròn lên của: giờ máy cần / giờ làm việc hữu ích của một máy">
                    <span className="VcHasTooltip">Máy cần</span>
                </Tooltip>
            ),
            dataIndex: 'machinesNeeded',
            align: 'right',
            width: 50,
            render: (value) => numberCell(value, formatInt),
        },
        {
            title: (
                <Tooltip title="Số máy đang có — Số máy thực tế đang có. Nhập tay, được lưu lại">
                    <span className="VcHasTooltip">Máy đang có</span>
                </Tooltip>
            ),
            key: 'machinesOnHand',
            align: 'right',
            width: 90,
            render: (text, row) => {
                if (row.pacing !== 'machine') return <span className="VcMuted">{DASH}</span>

                return (
                    <Tooltip
                        title={row.machineShortage
                            ? `Đang thiếu ${row.machinesNeeded - row.machinesOnHand} máy so với số máy cần`
                            : ''}
                    >
                        <InputNumber
                            size="small"
                            min={0}
                            disabled={!canSaveParams}
                            className={`VcCapacityInput ${row.machineShortage ? 'VcCapacityInput--short' : ''}`}
                            value={capacityValue(row, 'machinesOnHand')}
                            onChange={(value) => changeCapacity(row.stageCode, 'machinesOnHand', value)}
                            onBlur={() => commitCapacity(row, 'machinesOnHand')}
                            onPressEnter={() => commitCapacity(row, 'machinesOnHand')}
                        />
                    </Tooltip>
                )
            },
        },
        {
            title: (
                <Tooltip title="Số người quy đổi — Số người lý thuyết, có phần lẻ: 2,5 nghĩa là cần 2,5 người làm suốt khung thời gian mới xong">
                    <span className="VcHasTooltip">Người quy đổi</span>
                </Tooltip>
            ),
            dataIndex: 'fteEquivalent',
            align: 'right',
            width: 65,
            render: (value) => formatDecimal(value, 2),
        },
        {
            title: (
                <Tooltip title="Số người cần bố trí — Làm tròn lên của Số người quy đổi, vì không có nửa người">
                    <span className="VcHasTooltip">Người cần</span>
                </Tooltip>
            ),
            dataIndex: 'peopleNeeded',
            align: 'right',
            width: 60,
            render: (value) => <strong>{formatInt(value)}</strong>,
        },
        {
            title: (
                <Tooltip title="Số người đang có — Số người thực tế đang có. Nhập tay, được lưu lại">
                    <span className="VcHasTooltip">Người đang có</span>
                </Tooltip>
            ),
            key: 'peopleOnHand',
            align: 'right',
            width: 90,
            render: (text, row) => (
                <InputNumber
                    size="small"
                    min={0}
                    disabled={!canSaveParams}
                    className="VcCapacityInput"
                    value={capacityValue(row, 'peopleOnHand')}
                    onChange={(value) => changeCapacity(row.stageCode, 'peopleOnHand', value)}
                    onBlur={() => commitCapacity(row, 'peopleOnHand')}
                    onPressEnter={() => commitCapacity(row, 'peopleOnHand')}
                />
            ),
        },
        {
            title: (
                <Tooltip title="Thiếu / thừa người — Số người cần bố trí trừ số người đang có. Dương là thiếu, âm là thừa">
                    <span className="VcHasTooltip">Thiếu / thừa</span>
                </Tooltip>
            ),
            dataIndex: 'peopleGap',
            align: 'right',
            width: 55,
            // Chưa nhập số người đang có → dấu gạch, KHÔNG tính bằng 0 (BR-M17-37).
            render: (value) => numberCell(value, (gap) => (
                <span className={gap > 0 ? 'VcDanger' : 'VcSuccess'}>{formatSigned(gap)}</span>
            )),
        },
        {
            title: (
                <Tooltip title="Mức tải hiện tại — Số người quy đổi chia số người đang có. LỚN HƠN 1 LÀ ĐANG QUÁ TẢI">
                    <span className="VcHasTooltip">Mức tải</span>
                </Tooltip>
            ),
            dataIndex: 'currentLoad',
            align: 'right',
            width: 70,
            render: (value) => numberCell(value, (load) => (
                <span className={load > 1 ? 'VcOverload' : ''}>{formatDecimal(load, 2)} lần</span>
            )),
        },
        {
            title: (
                <Tooltip title="Đã có định mức — Phần trăm lượt qua công đoạn đã tra được định mức. Phần chưa tra được KHÔNG vào giờ công, nên con số giờ bên trái là của phần đã phủ">
                    <span className="VcHasTooltip">Có ĐM</span>
                </Tooltip>
            ),
            dataIndex: 'hasStandard',
            align: 'right',
            width: 70,
            render: (value) => <PercentTag ratio={value} kind="standard"/>,
        },
        {
            title: (
                <Tooltip title="Đã có dữ liệu file in — Phần trăm lượt qua công đoạn đã có dữ liệu file in. In, Cắt, Khắc tính giờ theo dữ liệu này nên thiếu nó là chưa tính được giờ">
                    <span className="VcHasTooltip">Có file in</span>
                </Tooltip>
            ),
            dataIndex: 'hasFileData',
            align: 'right',
            width: 55,
            render: (value) => <PercentTag ratio={value} kind="fileData"/>,
        },
    ]

    const renderKpis = () => {
        if (!report) return null

        const bottleneck = stages.find((stage) => stage.stageCode === report.bottleneckStageCode)

        const cards = [
            {
                label: 'Số item cần sản xuất',
                value: formatInt(report.itemCount),
                /*
                 * Nói ra phần KÉO THÊM ngay tại thẻ.
                 *
                 * ⚠️ Con số này là số THỰC TẾ đưa vào tính (`itemCount`), không phải tổng mục tiêu —
                 *    và nó phải như vậy, vì giờ công / số người / số máy ở các thẻ bên cạnh đều tính
                 *    trên đúng tập item này; hiện mục tiêu ở đây thì ba thẻ kia không cộng lại được.
                 *
                 *    Nhưng bảng tỷ lệ phía trên CỐ Ý chỉ in `target/qty` và giấu `actual` đi, nên
                 *    người dùng cộng các ô lại rồi thấy lệch với thẻ này mà không có gì giải thích —
                 *    đã xảy ra thật (1.159 + 37 = 1.196 so với 1.313). Dòng tóm tắt có nói, nhưng nó
                 *    nằm tít trên cùng và người ta so hai con số trước khi đọc.
                 */
                caption: pulledItemCount > 0
                    ? `${formatInt(report.photoRequestedItemCount)} item thuộc đơn cần chụp ảnh · gồm ${formatInt(pulledItemCount)} item kéo thêm do lấy trọn đơn`
                    : `${formatInt(report.photoRequestedItemCount)} item thuộc đơn cần chụp ảnh`,
            },
            {
                label: 'Số đơn',
                value: formatInt(report.orderCount),
                caption: 'số đơn khác nhau trong danh sách',
            },
            {
                label: 'Tổng giờ công',
                value: `${formatHours(totals.reworkedHours, 1)} h`,
                caption: `theo định mức ${formatHours(totals.standardHours, 1)} · làm lại +${formatHours((totals.reworkedHours || 0) - (totals.standardHours || 0), 1)}`,
            },
            {
                label: 'Tổng số người cần bố trí',
                value: formatInt(totals.peopleNeeded),
                // BR-M17-32: tổng của các số ĐÃ LÀM TRÒN từng công đoạn, nên luôn ≥ làm tròn của tổng.
                caption: `cộng số đã làm tròn từng công đoạn · quy đổi ${formatDecimal(totals.fteEquivalent, 2)}`,
            },
            {
                label: 'Đã có định mức / dữ liệu file in',
                value: (
                    <span>
                        <PercentTag ratio={totals.hasStandard} kind="standard" digits={1}/>
                        {' / '}
                        <PercentTag ratio={totals.hasFileData} kind="fileData" digits={1}/>
                    </span>
                ),
                caption: 'hai nguyên nhân khác nhau, hai bộ phận xử lý',
            },
            {
                label: 'Công đoạn nghẽn',
                value: bottleneck ? bottleneck.name : DASH,
                caption: bottleneck
                    ? `mức tải ${formatDecimal(bottleneck.currentLoad, 2)} lần · cần ${formatInt(bottleneck.peopleNeeded)} / đang có ${formatInt(bottleneck.peopleOnHand)}`
                    : 'chưa nhập số người đang có',
                tone: 'bad',
            },
        ]

        return (
            <div className="VcKpiStrip">
                {cards.map((card) => (
                    <div className={`VcKpiCard VcKpiCard--${card.tone || ''}`} key={card.label}>
                        <div className="VcKpiLabel">{card.label}</div>
                        <div className="VcKpiValue">{card.value}</div>
                        <div className="VcKpiCaption">{card.caption}</div>
                    </div>
                ))}
            </div>
        )
    }

    const renderNotices = () => {
        if (!report) return null

        const blocks = [
            <Notice dismissible key="b1" level="info">
                Nguồn lấy hàng: <strong>{WORKLOAD_SOURCES[source]}</strong>. Báo cáo tính trên tổng lượng
                hàng cần sản xuất, không trừ phần đã làm và không xét hàng đang làm dở. Đây là ước lượng
                người và máy để điều phối, không phải “còn bao nhiêu việc chưa làm”.
            </Notice>,
        ]

        /*
         * FR-M17-23 / BR-N3-18 — dải cảnh báo BẮT BUỘC của nguồn Sản lượng hiện tại.
         *
         * Không có nút tắt, và KHÔNG phụ thuộc hai tỷ lệ chất lượng dữ liệu (AC-23.1): rủi ro hiểu
         * sai ở nguồn này cao hơn hẳn hai nguồn cũ vì tỷ lệ hàng đang làm dở lớn hơn nhiều. Thiếu
         * dòng này, người dùng đọc số của nhóm quá hạn rồi kết luận thiếu người gấp đôi thực tế.
         */
        if (source === CURRENT_OUTPUT) {
            blocks.push(
                <Notice key="b0" level="warning">
                    {CURRENT_OUTPUT_WARNING}
                </Notice>
            )
        }

        /*
         * Item bị công tắc "Ngoại trừ: Hold ship, Request design" của màn hình nguồn loại ra, và item
         * còn hạn quá 4 ngày (ngoài bộ cột màn hình nguồn đang hiển thị). Nói ra để tổng của báo cáo
         * lệch tổng của màn hình nguồn thì người dùng biết phần lệch đi đâu, thay vì tự đoán.
         */
        if (source === CURRENT_OUTPUT && report.urgencyDrops) {
            const {outOfRange, excludedByToggle, conflict} = report.urgencyDrops

            if (outOfRange || excludedByToggle || conflict) {
                blocks.push(
                    <Notice dismissible key="b0b" level="info">
                        Không đưa vào tính: {formatInt(excludedByToggle)} item bị loại theo quy ước “Ngoại
                        trừ Hold ship, Request design” của nhóm Quá hạn sản xuất (do note) ·{' '}
                        {formatInt(outOfRange)} item còn hạn ngoài các cột đang hiển thị
                        {conflict > 0 && ` · ${formatInt(conflict)} item có dữ liệu mức khẩn mâu thuẫn`}.
                    </Notice>
                )
            }
        }

        // Hai cảnh báo tách rời: hai nguyên nhân khác nhau, hai bộ phận xử lý khác nhau (BR-M17-19).
        if (totals.hasStandard !== null && totals.hasStandard < 1) {
            blocks.push(
                <Notice dismissible key="b2" level="warning" action="Xem" onAction={() => setActiveTab('missingStandard')}>
                    Đã có định mức {formatPercent(totals.hasStandard, 1)} —{' '}
                    {formatInt((report.missingStandard || []).reduce((sum, row) => sum + row.itemCount, 0))} lượt
                    item không tra ra được định mức, thuộc {formatInt((report.missingStandard || []).length)} tổ
                    hợp SKU × công đoạn × vật liệu. Người đi xử lý: IE / Trưởng khu vực.
                </Notice>
            )
        }

        if (totals.hasFileData !== null && totals.hasFileData < 1) {
            blocks.push(
                <Notice dismissible key="b3" level="error" action="Xem" onAction={() => setActiveTab('missingFileData')}>
                    Đã có dữ liệu file in {formatPercent(totals.hasFileData, 1)} —{' '}
                    {formatInt((report.missingFileData || []).reduce((sum, row) => sum + row.itemCount, 0))} lượt
                    item chưa tạo file in nên không tính được giờ công của In, Cắt, Khắc. Người đi xử lý:
                    Designer / File operator. Đối chiếu với chỉ số “sẵn sàng / chờ file” trên màn hình
                    Thống kê item chưa phân lô.
                </Notice>
            )
        }

        if ((report.dataErrors || []).length) {
            blocks.push(
                <Notice dismissible key="b4" level="error">
                    Không lấy được routing của các SKU:{' '}
                    {report.dataErrors.map((item) => (item.skuIds || []).join(', ')).join(' · ')}. Item của
                    các SKU đó không được tính vào báo cáo.
                </Notice>
            )
        }

        // Item chưa có thời gian cam kết: VẪN được tính vào báo cáo (kể cả khi lọc theo nhóm cam kết),
        // chỉ đánh dấu để người đọc biết một phần lượng hàng chưa gắn nhóm — không âm thầm bỏ.
        if (report.unknownCommitmentItemCount > 0) {
            blocks.push(
                <Notice dismissible key="b5" level="warning">
                    {formatInt(report.unknownCommitmentItemCount)} item chưa có thời gian cam kết — vẫn
                    được tính vào báo cáo, nhưng không thuộc nhóm cam kết nào nên bộ lọc “Nhóm thời gian
                    cam kết” không loại chúng.
                </Notice>
            )
        }

        return blocks
    }

    return (
        <div className="VcPage VcWorkloadPage container-fluid">
            <div className="VcPageHead">
                <h1 className="VcPageTitle">Tính khối lượng công việc &amp; nhân lực theo công đoạn</h1>
            </div>

            <div className="VcFilterCard">
                <div className="VcFilterRow">
                    <div className="VcFilterItem">
                        <label>Nguồn lấy hàng</label>
                        <div className="VcSegmented">
                            {Object.keys(WORKLOAD_SOURCES).map((key) => (
                                <button
                                    type="button"
                                    key={key}
                                    className={source === key ? 'is-active' : ''}
                                    onClick={() => setFilter({source: key})}
                                >
                                    {WORKLOAD_SOURCES[key]}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/*
                      * Chỉ hiện ở nguồn "Chưa phân lô" (yêu cầu người dùng, 2026-09-16). Nguồn Sản
                      * lượng hiện tại tự phân loại theo mức khẩn (BR-N3-02 / AC-17.2); nguồn "Thống kê
                      * theo thời gian" đã có "Thời gian nhận hàng" làm bộ lọc chính. Để nhóm cam kết
                      * hiện mà không rõ có áp dụng hay không là mời người dùng bấm vào một nút mập mờ.
                      */}
                    {source === 'unbatched' && (
                        <div className="VcFilterItem">
                            <label>Nhóm thời gian cam kết</label>
                            <div className="VcChips">
                                {DUE_GROUPS.map((group) => (
                                    <button
                                        type="button"
                                        key={group.value}
                                        className={dueGroups.includes(group.value) ? 'is-active' : ''}
                                        onClick={() => toggleDueGroup(group.value)}
                                    >
                                        {group.text}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="VcFilterItem">
                        <label>
                            <Tooltip title={TARGET_DAYS_TOOLTIP}>
                                <span className="VcHasTooltip">Khung thời gian mục tiêu (ngày làm việc)</span>
                            </Tooltip>
                        </label>
                        <InputNumber
                            min={TARGET_DAYS_MIN}
                            step={0.5}
                            value={targetDays}
                            onChange={(value) => {
                                /*
                                 * `InputNumber` của antd KHÔNG kẹp `min` lúc gõ và phát cả CHUỖI:
                                 * gõ "0.5" phát lần lượt `0` rồi `0.5`, xoá trắng ô phát `''`.
                                 * `clean()` chỉ loại `undefined|null|''` nên `0` và `"0."` đi thẳng
                                 * ra API — mà `targetDays` là MẪU SỐ của toàn bộ phép tính nhân
                                 * lực — và đi cả vào URL, nên link chia sẻ mang theo mẫu số 0.
                                 *
                                 * Ngoài khoảng thì KHÔNG áp dụng, giữ giá trị hợp lệ trước đó —
                                 * cùng quy ước với `handleParamChange` cho các ô số còn lại.
                                 */
                                if (typeof value !== 'number' || !Number.isFinite(value)) return
                                if (value < TARGET_DAYS_MIN) return

                                // Sửa tay thì TẮT cơ chế tự điền cho tới khi tải lại màn hình (§4.3).
                                setFilter({autoTarget: false, targetDays: value})
                            }}
                        />
                        {/*
                          * Có đường BẬT LẠI, không chỉ báo là đã tắt.
                          *
                          * ⚠️ `autoTarget` chỉ về `true` khi bấm "Đặt lại" (xoá sạch mọi bộ lọc) hoặc
                          *    tải lại trang. Người dùng gõ thử một con số rồi muốn quay về giá trị tự
                          *    điền theo nhóm cam kết thì trước đây phải vứt luôn cả bộ lọc đang chọn —
                          *    nên trên thực tế họ tự gõ lại bằng tay, và gõ sai thì không có gì báo.
                          */}
                        {!autoTarget && (
                            <div className="VcHint">
                                Đã tắt tự điền{' '}
                                <Button
                                    type="link"
                                    size="small"
                                    className="VcInlineLink"
                                    onClick={() => setFilter({autoTarget: true, targetDays: autoTargetDays(dueGroups)})}
                                >
                                    Bật lại
                                </Button>
                            </div>
                        )}
                    </div>

                    <div className="VcFilterItem VcFilterItem--action">
                        <Button type="link" onClick={() => setShowParams(!showParams)}>
                            Tham số nâng cao {showParams ? <UpOutlined/> : <DownOutlined/>}
                        </Button>
                    </div>
                </div>

                {/*
                  * Tham số nâng cao đặt NGAY DƯỚI thanh lọc, không đẩy xuống cuối thẻ.
                  *
                  * Nó là đầu vào của phép tính chứ không phải phần phụ lục: số ca, giờ mỗi ca và tỷ
                  * lệ giờ hữu ích quyết định toàn bộ cột "số người cần bố trí". Đặt dưới cùng thì
                  * người dùng cuộn qua vùng nhập tỷ lệ mức khẩn (cao gần một màn hình) mới thấy,
                  * và mở nó ra là bảng kết quả bị đẩy khỏi tầm mắt.
                  */}
                {showParams && (
                    <div className="VcParamsBlock">
                        <WorkloadParamsPanel
                            params={params}
                            errors={paramErrors}
                            canSave={canSaveParams}
                            onChange={handleParamChange}
                            onCommit={commitParams}
                            stages={stages}
                            canSaveStages={canSaveParams}
                            stageReworkValue={reworkPercentValue}
                            onStageReworkChange={(stageCode, value) => changeCapacity(stageCode, 'reworkRate', value)}
                            onStageReworkCommit={commitReworkRate}
                            onLoadActual={() => setActualRange(lastWeekRange())}
                            saveSlot={canSaveParams && (
                                <ConfigSaveBar
                                    enabled={saveParamsConfig}
                                    onEnabledChange={setSaveParamsConfig}
                                    name={paramsName}
                                    onNameChange={setParamsName}
                                    onSave={handleSaveParams}
                                    saving={savingParamsConfig}
                                    onReset={handleResetParams}
                                    meta={paramsMeta}
                                    emptyText="Chưa ai lưu tham số dùng chung — màn hình đang chạy giá trị mặc định."
                                    history={paramsHistory}
                                    onLoad={(entry) => {
                                        setParams(coerceParams({...WORKLOAD_PARAM_DEFAULTS, ...entry.payload}))
                                        setParamErrors({})
                                        // `entry.name` là TUỲ CHỌN — ConfigSaveBar nhận diện bản lưu
                                        // không tên bằng người + thời điểm (`.filter(Boolean)`). Gán
                                        // thẳng vào state sẽ cho `null`, và `paramsName.trim()` lúc
                                        // lưu ném TypeError trước khi kịp tạo promise, nên không
                                        // `.catch` nào bắt và nút kẹt loading vĩnh viễn.
                                        setParamsName(entry.name || '')
                                        message.info(`Đã nạp ${entry.name ? `"${entry.name}"` : 'cấu hình đã lưu'} lên màn hình — bấm Lưu cấu hình nếu muốn áp cho mọi người`)
                                    }}
                                />
                            )}
                        />
                    </div>
                )}

                {source === CURRENT_OUTPUT ? (
                    <div className="VcSourceFilter VcSourceFilter--urgency">
                        {report && report.urgencyLayout ? (
                            <>
                                <UrgencyRatioPanel
                                    layout={report.urgencyLayout}
                                    ratios={ratios}
                                    counts={urgencyCounts}
                                    disabled={loading}
                                    onChange={(cellKey, value) => setRatios((prev) => ({...prev, [cellKey]: value}))}
                                />

                                {/*
                                  * "Lưu thành config" (yêu cầu người dùng, 2026-09-16) — mặc định KHÔNG
                                  * lưu. Lưu là lưu DÙNG CHUNG cả nhà máy (giống `pp_workload_config`),
                                  * nên bắt buộc phải TICK rồi mới bấm được, tránh ai đó vô tình đổi mặc
                                  * định của mọi người chỉ vì đang thử số trên máy mình.
                                  *
                                  * ⚠️ `canSaveParams &&` là chốt QUYỀN, khác hẳn cái tick ở trên —
                                  *    tick chống bấm NHẦM, chốt này chống THIẾU QUYỀN. Thiếu nó thì
                                  *    tài khoản `fac_ffm` (sidebar khai "vào xem ở chế độ chỉ đọc")
                                  *    vẫn ghi được cấu hình toàn nhà máy: mọi ô ghi khác của màn
                                  *    đều đã gác bằng `canSaveParams`, riêng thanh này lọt.
                                  */}
                                {canSaveParams && (
                                <ConfigSaveBar
                                    enabled={saveRatiosConfig}
                                    onEnabledChange={setSaveRatiosConfig}
                                    name={ratiosName}
                                    onNameChange={setRatiosName}
                                    onSave={handleSaveUrgencyRatios}
                                    saving={savingRatiosConfig}
                                    onReset={handleResetUrgencyRatios}
                                    meta={ratiosMeta}
                                    emptyText="Chưa ai lưu tỷ lệ dùng chung — màn hình đang chạy mặc định 100% cho mọi cột."
                                    history={ratiosHistory}
                                    onLoad={(entry) => {
                                        setRatios(entry.payload || {})
                                        setRatiosName(entry.name || '')
                                        message.info(`Đã nạp ${entry.name ? `"${entry.name}"` : 'cấu hình đã lưu'} lên màn hình — bấm Lưu cấu hình nếu muốn áp cho mọi người`)
                                    }}
                                />
                                )}

                                {/*
                                  * Công tắc lấy trọn đơn.
                                  *
                                  * Đặt NGAY TRÊN dòng tóm tắt vì dòng đó là chỗ duy nhất in ra con số
                                  * "kéo thêm" — bật/tắt rồi nhìn xuống là thấy ngay nó đổi bao nhiêu.
                                  *
                                  * ⚠️ Nhãn viết ở thể PHỦ ĐỊNH ("Bỏ...") và mặc định KHÔNG tích, để
                                  *    trạng thái mặc định là ô trống. Hộp tích sẵn mà lại là hành vi
                                  *    mặc định thì người dùng tưởng có ai đó đã bật nó lên.
                                  */}
                                <div className="VcWholeOrderToggle">
                                    <Checkbox
                                        checked={!wholeOrder}
                                        onChange={(event) => setFilter({wholeOrder: !event.target.checked})}
                                    >
                                        Bỏ “kéo thêm do trọn đơn”
                                    </Checkbox>
                                    <Tooltip title="Mặc định, quota rơi vào giữa một đơn thì hệ thống lấy trọn đơn — vì đơn chỉ giao được khi mọi item của nó xong. Bỏ đi thì số lượng bám sát đúng tỷ lệ đã đặt, nhưng những đơn bị cắt giữa chừng sẽ không giao trọn được trong kỳ này.">
                                        <span className="VcHasTooltip VcHint">số lượng sẽ bám sát tỷ lệ đã đặt, đổi lại đơn ra không trọn</span>
                                    </Tooltip>
                                </div>

                                {report.allocation && (
                                    <AllocationSummaryLine
                                        totals={report.allocation.totals}
                                        onOpenDetail={() => setActiveTab('allocation')}
                                    />
                                )}
                            </>
                        ) : (
                            <div className="VcHint">Đang tải bộ cột mức khẩn…</div>
                        )}
                    </div>
                ) : source === 'received' ? (
                    <div className="VcSourceFilter">
                        <div className="VcFilterItem">
                            <label>Thời gian nhận hàng</label>
                            {/*
                              * RangePicker thay cho hai DatePicker rời: `ranges` của antd cho sẵn
                              * chỗ đặt các mốc gợi ý, và một ô thì không bao giờ chọn được "từ" sau
                              * "đến" như hai ô rời cho phép.
                              */}
                            <DatePicker.RangePicker
                                showTime={{format: 'HH:mm'}}
                                format="HH:mm - DD/MM/YYYY"
                                allowClear={false}
                                ranges={presetRanges()}
                                value={[receivedFrom, receivedTo]}
                                onChange={(value) => {
                                    const [from, to] = value || []

                                    // Chuẩn hoá giây: ô chỉ hiện HH:mm nhưng giá trị gửi lên là mốc
                                    // ISO đầy đủ, và antd đặt giây = 0 cho CẢ HAI đầu. Không chỉnh thì
                                    // chọn "đến 23:59" làm mất 59 giây cuối của khoảng — hàng quét
                                    // trong khoảng đó không rơi vào báo cáo nào cả.
                                    if (from && to) {
                                        setFilter({receivedFrom: rangeStartOf(from), receivedTo: rangeEndOf(to)})
                                    }
                                }}
                            />
                        </div>
                        <div className="VcFilterItem">
                            <label>Lọc theo SKU</label>
                            <Select
                                mode="multiple"
                                style={{minWidth: 320}}
                                placeholder="Chọn tất cả"
                                value={skuIds}
                                /*
                                 * ⚠️ `skuOptions` do CHÍNH response báo cáo dựng nên nó đã đủ, không
                                 *    bị phân trang như `GET /skus`. Lọc tại chỗ ở đây là ĐÚNG, không
                                 *    cần hỏi server — đây là chỗ khác biệt với ô chọn SKU ở màn Cấu
                                 *    hình chỉ số.
                                 *
                                 *    Không khai `showSearch`/`optionFilterProp`: `mode="multiple"`
                                 *    của rc-select đã bật sẵn cả hai (`showSearch` mặc định theo
                                 *    `isMultiple`, `optionFilterProp` mặc định `'value'`). Khai lại
                                 *    là no-op, và từng khiến changelog mô tả một thay đổi không xảy ra.
                                 */
                                onChange={(value) => setFilter({skuIds: value})}
                            >
                                {((report && report.skuOptions) || []).map((item) => (
                                    <Option key={item.skuId} value={item.skuId}>
                                        {item.skuId} — {formatInt(item.itemCount)} item
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </div>
                ) : (
                    <div className="VcSourceFilter">
                        <div className="VcHint">
                            Danh sách lấy nguyên trạng từ màn hình Thống kê item chưa phân lô, không đổi
                            điều kiện lọc. Item dòng 2D và item chờ làm lại đã được màn hình đó loại sẵn.
                            Khung thời gian mục tiêu đang tự điền {formatDecimal(targetDays, 1)} ngày; sửa
                            tay sẽ tắt cơ chế tự điền.
                        </div>
                    </div>
                )}

                <div className="VcSourceDescription">{WORKLOAD_SOURCE_DESCRIPTIONS[source]}</div>
            </div>

            {error ? (
                <LoadError error={error} message="Không lấy được danh sách hàng. Vui lòng thử lại" onRetry={fetchReport}/>
            ) : (
                <>
                    {/*
                      * Dải cảnh báo và thẻ KPI cũng phải báo ĐANG TẢI.
                      *
                      * ⚠️ Trước đây chỉ bảng công đoạn có `loading`; hai khối này giữ nguyên số của
                      *    lượt tải TRƯỚC trong suốt thời gian gọi lại. Màn này tự gửi lại mỗi khi
                      *    người dùng sửa một ô tỷ lệ, nên có những quãng vài trăm ms mà "Tổng giờ
                      *    công" vẫn là số của tỷ lệ cũ — đọc như thể thay đổi vừa rồi không có tác
                      *    dụng gì, hoặc tệ hơn, như một con số đã cập nhật mà thật ra chưa.
                      *
                      * `delay` để lượt gọi nhanh không kịp nháy: gõ một ô tỷ lệ mà spinner chớp liên
                      * tục còn khó chịu hơn là không có.
                      */}
                    <Spin spinning={loading} delay={SPIN_DELAY_MS}>
                        {renderNotices()}
                        {renderKpis()}
                    </Spin>

                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        tabBarExtraContent={
                            <Button loading={exporting} onClick={handleExport} disabled={!stages.length}>
                                <DownloadOutlined/> Xuất Excel
                            </Button>
                        }
                    >
                        <TabPane tab="Theo công đoạn" key="stages">
                            <Table
                                className="VcTable VcWorkloadTable"
                                rowKey="stageCode"
                                columns={columns}
                                dataSource={stages}
                                loading={loading}
                                pagination={false}
                                size="middle"
                                /*
                                  * Khớp ĐÚNG tổng `width` 16 cột (1205). Bản trước khai 1260 trong
                                  * khi tổng là 1220 — dư 40px thành một dải trống bên phải bảng.
                                  */
                                scroll={{x: 1205}}
                                locale={{
                                    emptyText: (
                                        <div className="VcEmptyState">
                                            {/*
                                              * E-N3-01 / E-N3-07: hai lý do rỗng khác hẳn nhau ở nguồn
                                              * Sản lượng hiện tại, và lời khuyên "mở rộng khoảng thời
                                              * gian" của hai nguồn cũ là vô nghĩa ở đây — nguồn này
                                              * không có khoảng thời gian nào để mở rộng.
                                              */}
                                            {source === CURRENT_OUTPUT ? (
                                                report && report.allocation && report.allocation.totals.total > 0 ? (
                                                    <>
                                                        <div>Mọi cột đang đặt tỷ lệ 0%.</div>
                                                        <div className="VcHint">
                                                            Đặt tỷ lệ lớn hơn 0 cho ít nhất một cột mức
                                                            khẩn để hệ thống tính ra số người và số máy.
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div>Không còn item nào đang tồn.</div>
                                                        <div className="VcHint">
                                                            Toàn bộ hàng trên FAC đã hoàn thành sản xuất,
                                                            hoặc không item nào thuộc các cột mức khẩn
                                                            đang hiển thị.
                                                        </div>
                                                    </>
                                                )
                                            ) : (
                                                <>
                                                    <div>Không có item nào khớp bộ lọc.</div>
                                                    <div className="VcHint">
                                                        Thử mở rộng khoảng thời gian hoặc chọn thêm nhóm
                                                        thời gian cam kết.
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ),
                                }}
                                onRow={(row) => ({
                                    className: `VcRow--clickable${report && report.bottleneckStageCode === row.stageCode ? ' VcRow--bottleneck' : ''}`,
                                    onClick: (event) => {
                                        // Bấm ô nhập KHÔNG mở khung trượt — hai hành vi tách biệt (§4.4).
                                        if (event.target.closest('.VcCapacityInput')) return

                                        setDrawerStage(row)
                                    },
                                })}
                                footer={() => (stages.length ? (
                                    <div className="VcTableFoot">
                                        <div className="VcTotalRow">
                                            <span>TỔNG</span>
                                            <span>Số lượt: {formatInt(totals.throughput)}</span>
                                            <span>Giờ định mức: {formatHours(totals.standardHours, 1)}</span>
                                            <span>Đã tính làm lại: {formatHours(totals.reworkedHours, 1)}</span>
                                            <span>Quy đổi: {formatDecimal(totals.fteEquivalent, 2)}</span>
                                            <span>Cần bố trí: {formatInt(totals.peopleNeeded)}</span>
                                            <span>Đang có: {formatInt(totals.peopleOnHand)}</span>
                                            <span>
                                                Thiếu/thừa: {formatSigned(totals.peopleGap)}
                                            </span>
                                            <span>
                                                <PercentTag ratio={totals.hasStandard} kind="standard"/>
                                                {' · '}
                                                <PercentTag ratio={totals.hasFileData} kind="fileData"/>
                                            </span>
                                        </div>
                                        <div className="VcHint">
                                            Hai cột nền xanh nhạt là số thực tế nhập tay, được lưu dùng
                                            chung cho cả nhà máy. Tổng số người là tổng của các số đã làm
                                            tròn từng công đoạn nên luôn lớn hơn hoặc bằng làm tròn của
                                            tổng số người quy đổi — không ghép được người lẻ giữa hai công
                                            đoạn. Bấm một dòng để xem cách tính.
                                        </div>
                                    </div>
                                ) : null)}
                            />
                        </TabPane>

                        <TabPane
                            key="missingStandard"
                            tab={
                                <span>
                                    Thiếu định mức{' '}
                                    <Tag color="red">{formatInt(((report && report.missingStandard) || []).length)}</Tag>
                                </span>
                            }
                        >
                            <div className="VcHint mb-2">
                                Item đã có đủ dữ liệu file in nhưng không tra ra được định mức cho vật liệu
                                tương ứng. Người đi xử lý: IE / Trưởng khu vực — vào màn hình cấu hình định
                                mức để khai bổ sung cho vật liệu còn thiếu.
                            </div>
                            <Table
                                className="VcTable"
                                rowKey={(row) => `${row.skuId}-${row.stageCode}-${row.materialCode}`}
                                size="small"
                                pagination={false}
                                dataSource={(report && report.missingStandard) || []}
                                locale={{emptyText: 'Không có dòng nào.'}}
                                columns={[
                                    // KHÔNG đặt link ở cột SKU: dòng công đoạn tính theo ĐƠN có
                                    // skuId là `(tất cả đơn)` — SKU giả `__order__` của service, không
                                    // phải mã tra cứu được. Đơn vị hành động ở đây là CÔNG ĐOẠN
                                    // (nhóm định mức khai theo công đoạn + phạm vi NVL/SKU), nên link
                                    // gắn vào cột Công đoạn. Để `<code>` chọn/copy được như cũ.
                                    {title: 'SKU', dataIndex: 'skuId', render: (value) => <code>{value}</code>},
                                    {
                                        title: 'Công đoạn',
                                        dataIndex: 'stageName',
                                        // `<Link>` render ra <a href> THẬT nên ctrl/cmd+click mở tab
                                        // mới — bảng này có hàng chục dòng phải sửa lần lượt, dùng
                                        // history.push là mỗi lần quay lại mất sạch bộ lọc + khoảng
                                        // ngày và phải chạy lại báo cáo.
                                        render: (value, row) => (
                                            <Link
                                                to={stageConfigPath(row.stageCode)}
                                                title={`Mở cấu hình định mức công đoạn ${value}`}
                                            >
                                                {value}
                                            </Link>
                                        ),
                                    },
                                    {
                                        title: 'Vật liệu chưa có định mức',
                                        key: 'material',
                                        render: (text, row) => (
                                            <Tag color="red">{row.materialName} ({row.materialCode})</Tag>
                                        ),
                                    },
                                    {
                                        title: 'Số item ảnh hưởng',
                                        dataIndex: 'itemCount',
                                        align: 'right',
                                        render: formatInt,
                                        defaultSortOrder: 'descend',
                                        sorter: (left, right) => left.itemCount - right.itemCount,
                                    },
                                ]}
                            />
                        </TabPane>

                        <TabPane
                            key="missingFileData"
                            tab={
                                <span>
                                    Chưa có dữ liệu file in{' '}
                                    <Tag color="purple">{formatInt(((report && report.missingFileData) || []).length)}</Tag>
                                </span>
                            }
                        >
                            <div className="VcHint mb-2">
                                Item chưa tạo file in nên chưa có diện tích in / đường cắt / diện tích khắc.
                                Không tính được giờ công của In, Cắt, Khắc. Người đi xử lý: Designer / File
                                operator. Hệ thống không lấy diện tích trung bình của SKU để bù — mỗi item là
                                một thiết kế riêng nên giả định đó không đúng.
                            </div>
                            <Table
                                className="VcTable"
                                rowKey={(row) => `${row.skuId}-${row.stageCode}`}
                                size="small"
                                pagination={false}
                                dataSource={(report && report.missingFileData) || []}
                                locale={{emptyText: 'Toàn bộ item đã có dữ liệu file in.'}}
                                columns={[
                                    {title: 'SKU', dataIndex: 'skuId', render: (value) => <code>{value}</code>},
                                    {title: 'Công đoạn', dataIndex: 'stageName'},
                                    {
                                        title: 'Số item đang chờ file',
                                        dataIndex: 'itemCount',
                                        align: 'right',
                                        render: formatInt,
                                        defaultSortOrder: 'descend',
                                        sorter: (left, right) => left.itemCount - right.itemCount,
                                    },
                                ]}
                            />
                        </TabPane>

                        {/*
                          * Tab thứ sáu, CHỈ có ở nguồn Sản lượng hiện tại. Dựng có điều kiện thay vì
                          * `disabled`: ở hai nguồn còn lại nó không có nội dung nào để hiện, và một
                          * tab xám thường trực chỉ làm thanh tab dài thêm.
                          */}
                        {source === CURRENT_OUTPUT && report && report.allocation && (
                            <TabPane tab="Phân bổ theo mức khẩn" key="allocation">
                                <AllocationTab
                                    cells={report.allocation.cells}
                                    layout={report.urgencyLayout}
                                />
                            </TabPane>
                        )}

                        <TabPane tab="Giải thích chỉ số" key="glossary">
                            <h3 className="VcSectionTitle">Chỉ số kết quả</h3>
                            <Table
                                className="VcTable"
                                rowKey="label"
                                size="small"
                                pagination={false}
                                dataSource={CAPACITY_RESULT_METRICS}
                                columns={[
                                    {title: 'Tên chỉ số', dataIndex: 'label', width: 200},
                                    {
                                        title: 'Nghĩa',
                                        dataIndex: 'meaning',
                                        // Hai dòng bắt buộc in đậm phần chiều tốt xấu (§3.8).
                                        //
                                        // Cắt theo ĐỘ DÀI chứ không `value.replace(row.emphasis, '')`:
                                        // `meaning` viết HOA phần nhấn mạnh ("DƯƠNG LÀ THIẾU") còn
                                        // `emphasis` viết thường ("Dương là thiếu"), mà `String.replace`
                                        // phân biệt hoa/thường nên không cắt được gì — câu bị in lặp
                                        // thành "Dương là thiếuDƯƠNG LÀ THIẾU, âm là thừa".
                                        render: (value, row) => {
                                            const text = value || ''
                                            const lead = row.emphasis || ''

                                            if (!lead || text.toLowerCase().indexOf(lead.toLowerCase()) !== 0) {
                                                return text
                                            }

                                            return (
                                                <span>
                                                    <strong>{text.slice(0, lead.length)}</strong>
                                                    {text.slice(lead.length)}
                                                </span>
                                            )
                                        },
                                    },
                                    {title: 'Cách tính', dataIndex: 'formula'},
                                ]}
                            />

                            <h3 className="VcSectionTitle">Chỉ số chất lượng dữ liệu</h3>
                            <Table
                                className="VcTable"
                                rowKey="label"
                                size="small"
                                pagination={false}
                                dataSource={CAPACITY_DATA_METRICS}
                                columns={[
                                    {title: 'Tên chỉ số', dataIndex: 'label', width: 200},
                                    {title: 'Nghĩa', dataIndex: 'meaning'},
                                    {title: 'Ai đi xử lý khi thiếu', dataIndex: 'owner'},
                                ]}
                            />

                            <h3 className="VcSectionTitle">Tham số tính toán</h3>
                            <Table
                                className="VcTable"
                                rowKey="label"
                                size="small"
                                pagination={false}
                                dataSource={CAPACITY_PARAM_GLOSSARY}
                                columns={[
                                    {title: 'Tên tham số', dataIndex: 'label', width: 260},
                                    {title: 'Nghĩa', dataIndex: 'meaning'},
                                ]}
                            />
                        </TabPane>

                        <TabPane tab="Cách đọc báo cáo" key="reading">
                            <ul className="VcReadingList">
                                {CAPACITY_READING_NOTES.map((note) => <li key={note}>{note}</li>)}
                            </ul>
                        </TabPane>
                    </Tabs>
                </>
            )}

            {/*
              * Hỏi khoảng thời gian TRƯỚC khi đo, không lặng lẽ lấy một khoảng mặc định: con số này
              * sẽ được GHI xuống cấu hình dùng chung cả nhà máy, nên người bấm phải thấy rõ mình đang
              * đo trên khoảng nào.
              */}
            <Modal
                visible={!!actualRange}
                title="Nạp tỷ lệ làm lại từ dữ liệu thực tế"
                okText="Đo và ghi vào cấu hình"
                cancelText="Huỷ"
                confirmLoading={loadingActual}
                onOk={handleLoadActualRework}
                onCancel={() => setActualRange(null)}
            >
                <div className="VcHint" style={{marginBottom: 10}}>
                    Hệ thống đọc sản lượng thực tế trong khoảng đã chọn, lấy tỷ lệ{' '}
                    <b>số lần làm lại / tổng sản lượng</b> của từng công đoạn rồi ghi vào cấu hình.
                    Công đoạn không có sản lượng trong khoảng này sẽ được giữ nguyên, không đặt về 0.
                </div>
                <DatePicker.RangePicker
                    showTime={{format: 'HH:mm'}}
                    format="DD/MM/YYYY HH:mm"
                    allowClear={false}
                    ranges={presetRanges()}
                    value={actualRange}
                    onChange={(value) => {
                        const [next, nextTo] = value || []

                        if (next && nextTo) setActualRange([rangeStartOf(next), rangeEndOf(nextTo)])
                    }}
                    style={{width: '100%'}}
                />
            </Modal>

            <StageDetailDrawer
                visible={!!drawerStage}
                stageCode={drawerStage ? drawerStage.stageCode : null}
                stageName={drawerStage ? drawerStage.name : ''}
                params={queryParams}
                onClose={() => setDrawerStage(null)}
            />
        </div>
    )
}

export default WorkloadPage
